import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TreeStructure, ArrowClockwise, CheckCircle, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABEL = {
  group_winner: "Group Winner",
  group_runner_up: "Runner-up",
  best_third: "Best 3rd-placed",
  bye_slot: "BYE",
};

const SOURCE_COLOR = {
  group_winner: "bg-primary/20 text-primary border-primary/30",
  group_runner_up: "bg-chart-3/20 text-chart-3 border-chart-3/30",
  best_third: "bg-secondary/20 text-secondary border-secondary/30",
  bye_slot: "bg-muted text-muted-foreground border-border",
};

export default function ProgressionDialog({ open, onOpenChange, tournamentId, onFinalized }) {
  const [preview, setPreview] = useState(null);
  const [editableSeats, setEditableSeats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadPreview = async () => {
    if (!tournamentId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tournaments/${tournamentId}/progression-preview`);
      setPreview(res.data);
      setEditableSeats(res.data.seats || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load progression preview");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) loadPreview(); }, [open, tournamentId]); // eslint-disable-line

  const finalize = async () => {
    if (!preview || !editableSeats.length) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/tournaments/${tournamentId}/progression`, {
        seats: editableSeats,
        first_round: preview.first_round,
      }, { withCredentials: true });
      toast.success(`Bracket generated! ${editableSeats.length / 2} matches created.`);
      onFinalized?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to finalize progression");
    } finally { setSubmitting(false); }
  };

  // Build swap candidate list: each seat can be swapped with another non-bye seat of same or lower seed value
  const swapSeats = (i1, i2) => {
    setEditableSeats(prev => {
      const next = [...prev];
      [next[i1], next[i2]] = [next[i2], next[i1]];
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="progression-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TreeStructure className="w-5 h-5 text-primary" weight="fill" />
            Knockout Progression Preview
          </DialogTitle>
          <DialogDescription>
            Review qualifiers, byes and wildcards before the first knockout round is created.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-muted-foreground">Loading preview…</div>}

        {!loading && preview && preview.status === "group_stage_incomplete" && (
          <div className="py-6 text-center space-y-2">
            <Warning className="w-10 h-10 mx-auto text-secondary" weight="fill" />
            <p className="text-sm text-muted-foreground">{preview.message}</p>
          </div>
        )}

        {!loading && preview && preview.status === "ready" && (
          <div className="space-y-4">
            {preview.knockout_already_generated && (
              <div className="p-3 rounded-lg border border-secondary/40 bg-secondary/10 text-sm text-secondary">
                <Warning className="w-4 h-4 inline mr-1" weight="fill" />
                Knockout round has already been generated. Finalizing again will be rejected.
              </div>
            )}

            <SummaryBar preview={preview} />

            {preview.wildcards?.length > 0 && (
              <Section title={`Wildcards — Best 3rd-Placed (${preview.wildcards.length})`}
                       help="Selected by points → frame-diff → frames-for from the pool of 3rd-placed players.">
                <div className="grid grid-cols-2 gap-2">
                  {preview.wildcards.map(w => (
                    <Row key={`wc-${w.player_id}`} seed={w.seed} name={w.player_name}
                         source="best_third" sub={`${w.source_group} · ${w.points}pts · FD ${w.fd >= 0 ? '+' : ''}${w.fd}`} />
                  ))}
                </div>
              </Section>
            )}

            {preview.byes?.length > 0 && (
              <Section title={`Byes (${preview.byes.length})`}
                       help="Top-seeded qualifiers get a first-round pass when the bracket has spare slots.">
                <div className="grid grid-cols-2 gap-2">
                  {preview.byes.map(b => (
                    <Row key={`bye-${b.player_id}`} seed={b.seed} name={b.player_name}
                         source={b.source} sub={`${b.source_group || ''} · Top seed`} />
                  ))}
                </div>
              </Section>
            )}

            <Section title={`First Round: ${preview.first_round.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
                     help="Drag to swap seeds if you want to override the auto-seed. Pairs are consecutive (seat 1 vs 2, 3 vs 4…).">
              <div className="space-y-2">
                {editableSeats.reduce((acc, seat, i) => {
                  if (i % 2 === 0) acc.push([seat, editableSeats[i + 1], i]);
                  return acc;
                }, []).map(([s1, s2, i]) => (
                  <MatchPreview
                    key={`pair-${i}`}
                    s1={s1}
                    s2={s2}
                    allSeats={editableSeats}
                    seatIndex1={i}
                    seatIndex2={i + 1}
                    onSwap={swapSeats}
                  />
                ))}
              </div>
            </Section>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="progression-dialog-close">
            Cancel
          </Button>
          <Button variant="outline" onClick={loadPreview} disabled={loading} data-testid="progression-dialog-refresh">
            <ArrowClockwise className="w-4 h-4 mr-1" />Refresh
          </Button>
          <Button
            onClick={finalize}
            disabled={submitting || !preview || preview.status !== "ready" || preview.knockout_already_generated}
            data-testid="progression-dialog-finalize"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            {submitting ? "Creating…" : "Approve & Generate Bracket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryBar({ preview }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 rounded-lg bg-muted/20 border border-border">
      <Stat label="Qualifiers" value={preview.qualifiers.length} />
      <Stat label="Bracket Size" value={preview.bracket_size} />
      <Stat label="Gap" value={preview.gap} />
      <Stat label="Method" value={labelForMethod(preview.decision_method)} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-bold font-heading" data-testid={`progression-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
    </div>
  );
}

function labelForMethod(m) {
  return {
    clean_advance: "Clean",
    byes: "Byes",
    best_third: "Wildcards",
  }[m] || "—";
}

function Section({ title, help, children }) {
  return (
    <div>
      <h4 className="text-sm font-semibold flex items-center justify-between mb-1.5">
        <span>{title}</span>
      </h4>
      {help && <p className="text-[11px] text-muted-foreground mb-2">{help}</p>}
      {children}
    </div>
  );
}

function Row({ seed, name, source, sub }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded border border-border text-xs">
      <span className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-[10px] font-bold">#{seed}</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{name}</p>
        {sub && <p className="text-[10px] text-muted-foreground truncate">{sub}</p>}
      </div>
      <Badge className={`text-[9px] px-1.5 py-0.5 ${SOURCE_COLOR[source] || ''}`} variant="outline">
        {SOURCE_LABEL[source] || source}
      </Badge>
    </div>
  );
}

function MatchPreview({ s1, s2, allSeats, seatIndex1, seatIndex2, onSwap }) {
  const candidateOptions = allSeats
    .map((s, idx) => ({ ...s, _idx: idx }))
    .filter(s => s._idx !== seatIndex1 && s._idx !== seatIndex2 && !s.is_bye);
  return (
    <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-card">
      <SeatSide seat={s1} seatIndex={seatIndex1} candidates={candidateOptions} onSwap={onSwap} />
      <span className="text-muted-foreground font-bold text-xs">vs</span>
      <SeatSide seat={s2} seatIndex={seatIndex2} candidates={candidateOptions} onSwap={onSwap} />
    </div>
  );
}

function SeatSide({ seat, seatIndex, candidates, onSwap }) {
  if (seat.is_bye) {
    return (
      <div className="flex-1 text-center py-1.5 rounded bg-muted/50 text-muted-foreground text-xs font-mono">
        BYE
      </div>
    );
  }
  return (
    <div className="flex-1 flex items-center gap-2 min-w-0">
      <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold shrink-0">#{seat.seed}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" data-testid={`seat-name-${seatIndex}`}>{seat.player_name}</p>
        <p className="text-[10px] text-muted-foreground truncate">{SOURCE_LABEL[seat.source]} · {seat.source_group || '—'}</p>
      </div>
      {candidates.length > 0 && (
        <Select onValueChange={(v) => onSwap(seatIndex, parseInt(v, 10))}>
          <SelectTrigger className="h-7 w-28 text-[10px]" data-testid={`seat-swap-${seatIndex}`}>
            <SelectValue placeholder="Swap…" />
          </SelectTrigger>
          <SelectContent>
            {candidates.map(c => (
              <SelectItem key={c._idx} value={String(c._idx)}>
                Swap with {c.player_name} (#{c.seed})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
